import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import fetch from 'fetch-retry';
import date from 'date-and-time';
import { format, getDay, addDays } from 'date-fns';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import withStyles from '@material-ui/core/styles/withStyles';
import MuiButton from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import BackArrow from '@material-ui/icons/ArrowBackSharp';
import Timeline from '@material-ui/icons/Timeline';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon.js';
import NavPills from 'components/NavPills/NavPills';
import Button from '../../../components/CustomButtons/Button';
import Loading from '../../components/Loading/Loading';
import dashboardStyle from '../../assets/jss/views/dashboardStyle';
import { getNodes } from '../../stores/actions/actions';
import { GLOBAL_FETCH_SIGNAL, fetchUrl } from '../../stores/actions/settings';
import { getOrderedGraphableNodes } from '../../helpers/nodeSorting';
import { loaderStyleEmptyCard } from '../../helpers/CustomStyles';
import '../../components/NodeGraphCard/NodeGraphCard.css';

// no purples and pinks: they would clash with obstructed points
const PALETTE = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#8c564b', '#7f7f7f', '#bcbd22', '#17becf', '#000000', '#393b79'];
const OBSTRUCTED_COLOR = '#f058d4';
const colorForIndex = (i: number): string => (i < PALETTE.length ? PALETTE[i] : `hsl(${Math.round((i * 137.5) % 260)}, 65%, 40%)`);
// area above L1..L4 as on the single device chart, area below the lowest drawn level green
const LEVEL_COLORS = ['rgba(125, 242, 0, 0.4)', 'rgba(255, 225, 28, 0.4)', 'rgba(255, 153, 0, 0.4)', 'rgba(255, 0, 0, 0.4)'];
const BELOW_LEVELS_COLOR = 'rgba(49, 184, 0, 0.4)';

const isValidDate = (dateString: string | null): boolean => !!dateString && dateString.trim() !== '' && !isNaN(Date.parse(dateString));

const addIntervalToDateString = (dateString: string, interval: number): string => {
	const oldDate = new Date(dateString);
	const newDate = Math.abs(interval) === 7 ? date.addDays(oldDate, interval) : date.addMonths(oldDate, interval / 30);
	return newDate.toISOString();
};

interface ISystemGraphSeries {
	nodeId: number;
	timestamps: number[];
	deflPerc: (number | null)[];
	obstructed: boolean[];
	thresholdLevels: (number | null)[][];
	temperatures: (number | null)[];
}

const fetchSystemMeasurements = (systemId: number, start: string, end: string): Promise<Record<number, ISystemGraphSeries>> => {
	const token = localStorage.getItem('w4-token');
	return fetch(`${fetchUrl}/system-measurements`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		signal: GLOBAL_FETCH_SIGNAL,
		body: JSON.stringify({ SystemID: systemId, Start: start, End: end }),
		retries: 3,
		retryDelay: 1000,
		retryOn: (attempt, error, response) => error !== null || response.status >= 400,
	})
		.then((resp) => resp.json())
		.then((data: ISystemGraphSeries[]) => Object.fromEntries((data ?? []).map((series) => [series.nodeId, series])))
		.catch(() => ({}));
};
const formatPercent = (value: number): number => Number(Number(value).toFixed(1));

const movingAverage = (values: (number | null)[], fractionDigits: number, periods = 7): (number | null)[] => {
	if (values.every((v) => v === null)) return values;
	let lastValidVal = 0;
	return values.map((_, i) => {
		let sum = 0;
		let j = 0;
		for (; j < periods && i + j < values.length; j++) {
			const val = values[i + j];
			if (val !== null && !isNaN(val)) lastValidVal = val;
			sum += lastValidVal;
		}
		return Number((sum / j).toFixed(fractionDigits));
	});
};

const xTicksCallback = (measurementsRange: string) => (value, index, ticks) => {
	const tickDate = new Date(value);
	if (measurementsRange === '90') {
		const startDate = new Date(ticks[0].value);
		const offset = (7 - getDay(startDate) + 1) % 7;
		const adjustedDate = addDays(startDate, offset);
		const diffInDays = Math.floor((tickDate.getTime() - adjustedDate.getTime()) / (1000 * 60 * 60 * 24));
		if (diffInDays % 14 === 0) {
			return format(tickDate, 'dd-MM-yyyy');
		} else if (diffInDays % 7 === 0) {
			return '';
		}
	} else if (measurementsRange === '30') {
		return getDay(tickDate) === 1 ? format(tickDate, 'dd-MM-yyyy') : '';
	} else if (measurementsRange === '7') {
		if (tickDate.getHours() === 0 && tickDate.getMinutes() === 0) {
			return format(tickDate, 'dd-MM-yyyy');
		} else if (tickDate.getHours() % 6 === 0 && tickDate.getMinutes() === 0) {
			return '';
		}
	}
	return null;
};

const SystemGraphPage = ({ classes, match, location, history }) => {
	const dispatch = useDispatch();
	const nodes = useSelector((state: any) => state.dashboard.nodes);
	const mobileView = useSelector((state: any) => state.dashboard.mobileView);
	const systemId = parseInt(match?.params?.id);

	const [seriesByNode, setSeriesByNode] = useState<Record<number, ISystemGraphSeries>>({});
	const [measurementsLoading, setMeasurementsLoading] = useState(true);
	const [nodesLoaded, setNodesLoaded] = useState(false);
	const [showPoints, setShowPoints] = useState(false);
	const [hiddenNodeIds, setHiddenNodeIds] = useState<number[]>([]);
	const loading = measurementsLoading || !nodesLoaded;
	const requestCounter = useRef(0);
	const deflPercChartRef = useRef(null);
	const temperatureChartRef = useRef(null);

	const searchParams = new URLSearchParams(location.search);
	const from = searchParams.get('from');
	const to = searchParams.get('to');
	const measurementsRange = searchParams.get('days');
	const activeTab = parseInt(searchParams.get('tab')) || 0;

	const systemNodes = useMemo(() => (nodes ?? []).filter((n) => n.systemId === systemId), [nodes, systemId]);
	const graphableNodes = useMemo(
		() => getOrderedGraphableNodes(systemNodes).filter((n) => n.DeviceRole !== 'gateway'),
		[systemNodes],
	);
	const systems = useSelector((state: any) => state.dashboard.systems);
	const systemsVeryShort = useSelector((state: any) => state.dashboard.systemsVeryShort);
	const storedSystemName = [...(systems ?? []), ...(systemsVeryShort ?? [])].find((sys) => sys.ID === systemId)?.name;
	const [fetchedSystemName, setFetchedSystemName] = useState('');
	const systemName = storedSystemName ?? fetchedSystemName;

	useEffect(() => {
		if (isNaN(systemId) || storedSystemName) return;
		fetch(`${fetchUrl}/system/${systemId}`, {
			method: 'GET',
			headers: { Authorization: `Bearer ${localStorage.getItem('w4-token')}` },
			signal: GLOBAL_FETCH_SIGNAL,
		})
			.then((resp) => resp.json())
			.then((data) => setFetchedSystemName(data?.name ?? ''))
			.catch(() => {});
	}, [systemId]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!isNaN(systemId)) {
			setNodesLoaded(false);
			Promise.resolve(dispatch(getNodes([systemId]))).then(() => setNodesLoaded(true));
		}
	}, [systemId]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (!['0', '1'].includes(params.get('tab'))) {
			params.set('tab', '0');
		}
		if (!['7', '30', '90'].includes(params.get('days'))) {
			params.set('days', '30');
		}
		if (!isValidDate(params.get('to'))) {
			params.set('to', new Date().toISOString());
		}
		if (!isValidDate(params.get('from'))) {
			params.set('from', addIntervalToDateString(params.get('to'), -parseInt(params.get('days'))));
		}
		if (params.toString() !== new URLSearchParams(location.search).toString()) {
			history.replace(`${location.pathname}?${params.toString()}`);
		}
	}, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isNaN(systemId) || !isValidDate(from) || !isValidDate(to)) {
			return;
		}
		const requestId = ++requestCounter.current;
		setMeasurementsLoading(true);
		fetchSystemMeasurements(systemId, from, to).then((result) => {
			// ignore responses of an outdated range
			if (requestId !== requestCounter.current) return;
			setSeriesByNode(result);
			setMeasurementsLoading(false);
		});
	}, [systemId, from, to]);

	const resetZoom = () => {
		deflPercChartRef.current?.resetZoom();
		temperatureChartRef.current?.resetZoom();
	};

	const handleTabChange = (newTab) => {
		const params = new URLSearchParams(location.search);
		params.set('tab', newTab);
		history.push(`${location.pathname}?${params.toString()}`);
	};

	const handleDateRangeChange = (newRange: number | null, arrowButton: 'prev' | 'next' | null) => {
		const params = new URLSearchParams(location.search);
		const currentRange = parseInt(params.get('days'));
		if (arrowButton === null) {
			params.set('days', String(newRange));
			params.set('from', addIntervalToDateString(params.get('to'), -newRange));
		} else {
			const shift = arrowButton === 'next' ? currentRange : -currentRange;
			params.set('to', addIntervalToDateString(params.get('to'), shift));
			params.set('from', addIntervalToDateString(params.get('from'), shift));
		}
		history.push(`${location.pathname}?${params.toString()}`);
	};

	const toPoints = (series: ISystemGraphSeries, values: (number | null)[]) => series.timestamps.map((x, i) => ({ x, y: values[i] }));
	const lineStyle = (color: string) => ({
		borderColor: color,
		backgroundColor: color,
		borderWidth: 1.5,
		// set explicitly in both modes: react-chartjs-2 merges new dataset options into the old ones
		showLine: !showPoints,
		pointRadius: showPoints ? 2 : 0,
		hitRadius: 5,
		spanGaps: false,
		order: 0,
		yAxisID: 'y',
	});
	const deviceLabel = (node) =>
		['laser', 'inclinometer'].includes(node.MainSensorType) ? `${node.name} (${node.MainSensorType})` : node.name;

	// devices without base or permissible value have no deflection in % and are left out of its chart
	const entries = graphableNodes
		.map((node, i) => ({ node, series: seriesByNode[node.id], color: colorForIndex(i) }))
		.filter((e) => e.series);
	const deflPercEntries = entries.filter((e) => e.series.deflPerc.some((v) => v !== null));
	const temperatureEntries = entries.filter((e) => e.series.temperatures.some((v) => v !== null));
	const chartEntries = entries.filter((e) => deflPercEntries.includes(e) || temperatureEntries.includes(e));
	const isVisible = (e) => !hiddenNodeIds.includes(e.node.id);
	const visibleDeflPercEntries = deflPercEntries.filter(isVisible);

	const deflPercDatasets = visibleDeflPercEntries.map((e) => {
		const values = showPoints ? e.series.deflPerc : movingAverage(e.series.deflPerc, 1);
		// obstructed measurements purple, as on the single device chart
		const pointColors = showPoints ? e.series.obstructed.map((obstructed) => (obstructed ? OBSTRUCTED_COLOR : e.color)) : e.color;
		return {
			label: deviceLabel(e.node),
			data: toPoints(e.series, values),
			...lineStyle(e.color),
			pointBackgroundColor: pointColors,
			pointBorderColor: pointColors,
		};
	});
	const temperatureDatasets = temperatureEntries
		.filter(isVisible)
		.map((e) => ({ label: deviceLabel(e.node), data: toPoints(e.series, e.series.temperatures), ...lineStyle(e.color) }));

	// a level is drawn only when all visible measurements of all visible devices have the same value of it
	const commonLevels = [0, 1, 2, 3]
		.map((level) => {
			const values = visibleDeflPercEntries.flatMap((e) =>
				e.series.thresholdLevels.filter((_, i) => e.series.deflPerc[i] !== null).map((levels) => levels[level]),
			);
			const common = values.length > 0 && values[0] !== null && values.every((v) => v === values[0]);
			return common ? { level, value: values[0] } : null;
		})
		.filter((l) => l !== null)
		.sort((a, b) => a.value - b.value);

	const thresholdDatasets = [];
	if (commonLevels.length) {
		const deflPercValues = visibleDeflPercEntries.flatMap((e) => e.series.deflPerc).filter((v) => v !== null);
		const drawNegative = deflPercValues.some((v) => v < 0);
		const drawPositive = deflPercValues.some((v) => v > 0) || !drawNegative;
		// each band fills from its threshold line to the previous one (by dataset index), the lowest one to 0
		const addBand = (label: string, value: number, fill, color: string) => {
			thresholdDatasets.push({
				label,
				data: [{ x: new Date(from).getTime(), y: value }, { x: new Date(to).getTime(), y: value }],
				isThresholdBand: true,
				yAxisID: 'y',
				pointRadius: 0,
				hitRadius: 0,
				borderWidth: 0,
				backgroundColor: color,
				spanGaps: true,
				showLine: true,
				fill,
				order: 1,
			});
			return thresholdDatasets.length - 1;
		};
		[
			{ sign: 1, draw: drawPositive, outer: 'end' },
			{ sign: -1, draw: drawNegative, outer: 'start' },
		]
			.filter((side) => side.draw)
			.forEach(({ sign, outer }) => {
				const prefix = sign > 0 ? '' : '-';
				let previousIndex = addBand(`${prefix}below L${commonLevels[0].level + 1}`, sign * commonLevels[0].value, 'origin', BELOW_LEVELS_COLOR);
				commonLevels.slice(1).forEach((l, i) => {
					previousIndex = addBand(`${prefix}L${l.level + 1}`, sign * l.value, previousIndex, LEVEL_COLORS[commonLevels[i].level]);
				});
				const highest = commonLevels[commonLevels.length - 1];
				addBand(`${prefix}above L${highest.level + 1}`, sign * highest.value, outer, LEVEL_COLORS[highest.level]);
			});
	}

	const xScale = {
		type: 'time',
		min: from,
		max: to,
		time: {
			unit: measurementsRange === '7' ? 'hour' : 'day',
			displayFormats: { day: 'yyyy-MM-dd', hour: 'yyyy-MM-dd HH:mm' },
		},
		ticks: {
			font: { size: mobileView ? 8 : 12 },
			maxTicksLimit: mobileView ? 16 : 60,
			maxRotation: 0,
			callback: xTicksCallback(measurementsRange),
		},
	};
	const yTicks = { font: { size: mobileView ? 10 : 12 }, includeBounds: false };

	const chartOptions = (scales, unit: string, formatValue: (value: number) => number | string = (value) => value) => ({
		responsive: true,
		animation: false,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: true,
				position: 'bottom',
				// devices are switched on and off with the checkboxes
				onClick: () => {},
				labels: {
					boxWidth: 12,
					font: { size: mobileView ? 10 : 12 },
					filter: (item, data) => !data.datasets[item.datasetIndex].isThresholdBand,
				},
			},
			tooltip: {
				filter: (context) => !context.dataset.isThresholdBand,
				callbacks: {
					title: (context) =>
						context[0]?.parsed?.x
							? new Date(context[0].parsed.x).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
							: '',
					label: (context) => {
						const value = context.parsed.y === null || context.parsed.y === undefined ? '' : formatValue(context.parsed.y);
						return `${context.dataset.label}: ${value}${unit}`;
					},
				},
			},
			zoom: {
				zoom: { wheel: { enabled: false }, pinch: { enabled: mobileView }, mode: 'xy' },
				pan: { enabled: true, mode: 'xy' },
				limits: {
					x: { min: 'original', max: 'original', minRange: 10 * 6 * 10 * 60 * 1000 },
					y: { min: 'original', max: 'original' },
				},
			},
		},
		scales,
		onClick: (e) => {
			const chart = e.chart;
			chart.options.plugins.zoom.zoom.wheel.enabled = !chart.options.plugins.zoom.zoom.wheel.enabled;
			chart.update();
		},
	});

	const deflPercChartOptions = chartOptions(
		{ x: xScale, y: { grace: '5%', ticks: { ...yTicks, precision: 1, callback: formatPercent } } },
		'%',
		formatPercent,
	);
	const temperatureChartOptions = chartOptions({ x: xScale, y: { min: -20, max: 60, ticks: yTicks } }, '°C');

	const toggleNode = (nodeId: number) =>
		setHiddenNodeIds(hiddenNodeIds.includes(nodeId) ? hiddenNodeIds.filter((id) => id !== nodeId) : [...hiddenNodeIds, nodeId]);
	const deviceCheckboxes = !loading && chartEntries.length ? (
		<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', margin: '10px 0' }}>
			<MuiButton size='small' onClick={() => setHiddenNodeIds([])}>
				All
			</MuiButton>
			<MuiButton size='small' onClick={() => setHiddenNodeIds(chartEntries.map((e) => e.node.id))} style={{ marginRight: '10px' }}>
				None
			</MuiButton>
			{chartEntries.map((e) => (
				<FormControlLabel
					key={e.node.id}
					label={deviceLabel(e.node)}
					style={{ color: 'rgba(53, 53, 53, 0.87)', marginRight: '16px' }}
					control={<Checkbox size='small' style={{ color: e.color }} checked={isVisible(e)} onChange={() => toggleNode(e.node.id)} />}
				/>
			))}
		</div>
	) : null;

	const renderChart = (datasets, options, ref, bandDatasets = []) => {
		if (loading) {
			return (
				<div className='deflection-chart-container' style={{ position: 'relative', margin: 'auto', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Loading transform={0.85} width={70} height={mobileView ? 300 : 600} color='gray' />
				</div>
			);
		}
		if (!datasets.length) {
			const startString = date.format(new Date(from), 'DD/MM/YYYY HH:mm:ss');
			const endString = date.format(new Date(to), 'DD/MM/YYYY HH:mm:ss');
			return (
				<h3 style={{ display: 'flex', justifyContent: 'center', minHeight: mobileView ? '250px' : '600px', fontSize: mobileView ? '14px' : '18px' }}>
					No data for selected period <br />({startString} - {endString})
				</h3>
			);
		}
		return (
			<>
				<div className='deflection-chart-container' style={{ position: 'relative', margin: 'auto', width: '100%' }}>
					<Line className='deflectionChart' options={options} data={{ datasets: [...bandDatasets, ...datasets] }} plugins={[zoomPlugin]} ref={ref} />
				</div>
				<div style={{ justifyContent: 'center', display: 'flex' }}>
					<Button className='deflectionButton' color='info' onClick={resetZoom} size='sm' style={{ borderRadius: '20px', fontWeight: '500' }}>
						Zoom out
					</Button>
					<Button className='deflectionButton' color='info' onClick={() => setShowPoints(!showPoints)} size='sm' style={{ borderRadius: '20px', fontWeight: '500' }}>
						{showPoints ? 'show average' : 'show points'}
					</Button>
				</div>
			</>
		);
	};

	const rangeButton = (content, onClick, disabled, className = 'deflectionButtonRange') => (
		<Button
			className={`deflectionButton ${className}`}
			color='info'
			onClick={onClick}
			size='sm'
			style={{ borderRadius: '50px', fontWeight: '400' }}
			disabled={disabled}
		>
			{content}
		</Button>
	);
	const rangeButtons = (
		<div style={{ justifyContent: 'center', display: 'flex' }}>
			{rangeButton(<KeyboardArrowLeft />, () => handleDateRangeChange(null, 'prev'), loading, 'deflectionButtonFetch')}
			{rangeButton('1 week', () => handleDateRangeChange(7, null), measurementsRange === '7' || loading)}
			{rangeButton('1 month', () => handleDateRangeChange(30, null), measurementsRange === '30' || loading)}
			{rangeButton('3 months', () => handleDateRangeChange(90, null), measurementsRange === '90' || loading)}
			{rangeButton(<KeyboardArrowRight />, () => handleDateRangeChange(null, 'next'), loading, 'deflectionButtonFetch')}
		</div>
	);

	if (isNaN(systemId)) {
		return <div style={{ padding: '10px', ...loaderStyleEmptyCard }}>403</div>;
	}

	const tabs = [
		{ tabLabel: 'DEFLECTION [%]', tabContent: renderChart(deflPercDatasets, deflPercChartOptions, deflPercChartRef, thresholdDatasets) },
		{ tabLabel: 'TEMPERATURE [°C]', tabContent: renderChart(temperatureDatasets, temperatureChartOptions, temperatureChartRef) },
	];

	return (
		<GridContainer>
			<GridItem xs={12}>
				<NavLink to={`/systems/${systemId}/dashboard`}>
					<MuiButton size='small'>
						<BackArrow /> Dashboard
					</MuiButton>
				</NavLink>
				<Card>
					<CardHeader icon>
						<CardIcon color='info'>
							<Timeline />
						</CardIcon>
						<p className={classes.cardCategory}>System - All devices</p>
						{mobileView ? <h4 className={classes.cardTitle}>{systemName}</h4> : <h3 className={classes.cardTitle}>{systemName}</h3>}
					</CardHeader>
					<CardBody className='node-graph-card'>
						<NavPills variant='fullWidth' color='info' alignCenter={true} tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} />
						{rangeButtons}
						{deviceCheckboxes}
					</CardBody>
				</Card>
			</GridItem>
		</GridContainer>
	);
};

export default withStyles(dashboardStyle)(SystemGraphPage);
